import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { comma, timeAgo } from '@/lib/format';

interface Exchange {
  id: number;
  item_code: string;
  item_name: string;
  item_type: string;
  point_amount: number;
  store_name: string | null;
  status: string;
  status_reason: string | null;
  nickname: string;
  created_at: string;
  processed_at: string | null;
  viewed_at: string | null;
  expires_at: string | null;
  external_id: string | null;
  user_id: string;
  user_joined_at: string;
  email: string | null;
  point_balance: number;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'unviewed' | 'rejected' | 'refunded' | 'expired' | 'failed';

const STATUS_TABS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '대기중' },
  { key: 'approved', label: '승인' },
  { key: 'unviewed', label: '미수령' },
  { key: 'rejected', label: '거절' },
  { key: 'refunded', label: '환불' },
  { key: 'expired', label: '만료' },
  { key: 'failed', label: '실패' },
];

const STATUS_BADGE: Record<string, { label: string; variant: 'rise' | 'fall' | 'editor' | 'default' | 'outline' }> = {
  pending: { label: '대기중', variant: 'editor' },
  hold: { label: '보류', variant: 'editor' },
  approved: { label: '승인', variant: 'rise' },
  auto_issued: { label: '자동지급', variant: 'rise' },
  rejected: { label: '거절', variant: 'fall' },
  failed: { label: '실패', variant: 'fall' },
  refunded: { label: '환불', variant: 'default' },
  expired: { label: '만료', variant: 'default' },
};

/** YYYY.MM.DD HH:mm — 어드민 표시용 */
function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 승인 항목 유효기간(29일) 상태 계산.
 *  - 'expired'  : 이미 만료 (expires_at 지남)
 *  - 'imminent' : 3일 이내 만료 임박
 *  - 'normal'   : 그 외 — 남은 일/시간 표시
 *  - null       : 표시 불필요 (expires_at 없음)
 */
function getApprovedExpiryStatus(expiresAtIso: string | null):
  | { kind: 'expired' }
  | { kind: 'imminent'; label: string }
  | { kind: 'normal'; label: string }
  | null {
  if (!expiresAtIso) return null;
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return { kind: 'expired' };
  const hours = Math.floor(ms / 3_600_000);
  const days = Math.floor(hours / 24);
  const label = days >= 1 ? `${days}일 ${hours % 24}시간 남음` : `${hours}시간 남음`;
  if (days < 3) return { kind: 'imminent', label };
  return { kind: 'normal', label };
}

export function AdminExchangesPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [approving, setApproving] = useState<number | null>(null);
  const [externalId, setExternalId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'exchanges', statusFilter],
    queryFn: () => get<{ list: Exchange[] }>(`/admin/exchanges?status=${statusFilter}`),
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => post(`/admin/exchanges/${id}/approve`, { externalId }),
    onSuccess: () => {
      setApproving(null);
      setExternalId('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'exchanges'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (params: { id: number; reason: string }) =>
      post(`/admin/exchanges/${params.id}/reject`, { reason: params.reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'exchanges'] }),
  });

  return (
    <div className="w-full">
      <h1 className="mb-4 text-2xl font-extrabold">교환 승인</h1>

      {/* 상태 탭 — 처리된 항목도 화면에서 사라지지 않도록 기본값 '전체' */}
      <div className="mb-4 flex flex-wrap gap-1.5 border-b border-[var(--color-border)] pb-3">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatusFilter(t.key)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              statusFilter === t.key
                ? 'bg-[var(--color-primary)] text-[var(--color-primary-inv)] font-semibold'
                : 'text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <Skeleton className="h-96" />}

      <div className="space-y-3">
        {data?.list?.map((e) => {
          const isPending = e.status === 'pending' || e.status === 'hold';
          const badge = STATUS_BADGE[e.status] ?? { label: e.status, variant: 'default' as const };
          // approved 항목만 — 29일 유효기간 잔여/만료 임박 여부
          const expiry = e.status === 'approved' ? getApprovedExpiryStatus(e.expires_at) : null;
          return (
            <div
              key={e.id}
              className={`rounded-xl border p-4 ${
                isPending
                  ? 'border-[var(--color-border)] bg-[var(--color-surface)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg)] opacity-90'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <div className="font-semibold">{e.item_name}</div>
                    {e.store_name && (
                      /* 운영자가 한눈에 어느 편의점 상품권을 구매해야 하는지 식별 — 강조 색 */
                      <Badge variant="primary">{e.store_name}</Badge>
                    )}
                    {/* 승인 항목 — 유효기간 잔여/만료 상태 뱃지 (29일 정책) */}
                    {expiry?.kind === 'expired' && (
                      <Badge variant="fall">기간 만료</Badge>
                    )}
                    {expiry?.kind === 'imminent' && (
                      <Badge variant="fall">기간 만료 임박 · {expiry.label}</Badge>
                    )}
                    {expiry?.kind === 'normal' && (
                      <Badge variant="outline">{expiry.label}</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                    {comma(e.point_amount)}P 차감 · 신청 {timeAgo(e.created_at)}
                    {e.processed_at && (
                      <span> · 처리 {timeAgo(e.processed_at)}</span>
                    )}
                  </div>
                  {e.status_reason && (
                    <div className="mt-1 text-xs text-[var(--color-fall)]">
                      사유: {e.status_reason}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-1 gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-xs sm:grid-cols-2">
                    <div>
                      <span className="text-[var(--color-text-sub)]">닉네임</span>{' '}
                      <span className="font-medium">{e.nickname}</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-sub)]">보유 포인트</span>{' '}
                      <span className="font-medium">{comma(e.point_balance ?? 0)}P</span>
                    </div>
                    <div className="break-all">
                      <span className="text-[var(--color-text-sub)]">이메일</span>{' '}
                      <span className="font-medium">{e.email ?? '-'}</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-sub)]">가입일</span>{' '}
                      <span className="font-medium">
                        {e.user_joined_at
                          ? new Date(e.user_joined_at).toISOString().slice(0, 10)
                          : '-'}
                      </span>
                    </div>
                    {/* 승인 상태에서만 만료/수령 정보 노출 */}
                    {e.status === 'approved' && (
                      <>
                        <div>
                          <span className="text-[var(--color-text-sub)]">만료일</span>{' '}
                          <span className="font-medium">
                            {fmtDateTime(e.expires_at)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-sub)]">수령</span>{' '}
                          {e.viewed_at ? (
                            <span className="font-medium text-[var(--color-rise)]">
                              {fmtDateTime(e.viewed_at)} 확인
                            </span>
                          ) : (
                            <span className="font-medium text-[var(--color-fall)]">
                              미수령
                            </span>
                          )}
                        </div>
                        <div className="sm:col-span-2 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
                          유효기간: 발급일(승인일)로부터 29일 · 상품권 유효기간 약 29~30일 (미사용 시 소멸·환불 불가)
                        </div>
                      </>
                    )}
                    {/* 발급 번호 — 승인/만료 상태에서 어드민에게 노출 (감사용) */}
                    {(e.status === 'approved' || e.status === 'expired') && e.external_id && (
                      <div className="break-all sm:col-span-2">
                        <span className="text-[var(--color-text-sub)]">발급 번호</span>{' '}
                        <span className="font-mono text-[10px]">{e.external_id}</span>
                      </div>
                    )}
                    <div className="break-all sm:col-span-2">
                      <span className="text-[var(--color-text-sub)]">user_id</span>{' '}
                      <span className="font-mono text-[10px]">{e.user_id}</span>
                    </div>
                  </div>
                </div>
                {isPending ? (
                  approving === e.id ? (
                    <div className="flex gap-2">
                      <Input
                        placeholder="쿠폰 번호"
                        value={externalId}
                        onChange={(ev) => setExternalId(ev.target.value)}
                        className="w-40"
                      />
                      <Button
                        size="sm"
                        loading={approveMutation.isPending}
                        onClick={() => approveMutation.mutate(e.id)}
                        disabled={!externalId}
                      >
                        확정
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setApproving(null)}>
                        취소
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setApproving(e.id)}>
                        승인
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const reason = prompt('거절 사유');
                          if (reason) rejectMutation.mutate({ id: e.id, reason });
                        }}
                      >
                        거절
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="text-xs text-[var(--color-text-sub)]">처리 완료</div>
                )}
              </div>
            </div>
          );
        })}
        {data?.list?.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
            {statusFilter === 'pending'
              ? '대기 중인 교환 신청이 없어요.'
              : statusFilter === 'unviewed'
                ? '미수령(승인 후 미확인) 건이 없어요.'
                : '해당 상태의 교환 신청이 없어요.'}
          </div>
        )}
      </div>
    </div>
  );
}
